import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, Col, Input, Label, Row } from 'reactstrap';
import Breadcrumb from '../../../components/common/breadcrumb';
import { getCompanies, getSites, saveCompany, saveSite } from '../../devir-teslim/api';
import '../../devir-teslim/devir-teslim.css';

// Depo Yönetimi > Saha ve Operatörler: a site's type (depot, factory compound, port, free zone) and main operator, each
// zone's function, an operator per function (PDI by CAR Concept, quay by the port operator …). Custody follows the zone:
// the custody locations below are kept in step automatically, and a move between zones run by different companies is a
// change of custody. Changes are logged (custody_management.code_change_log).
const SITE_TYPE = { DEPO: 'Depo', FABRIKA: 'Fabrika sahası', LIMAN: 'Liman', SERBEST_BOLGE: 'Serbest bölge sahası', SANAL: 'Sanal liste (eski)' };

const SahaOperatorler = () => {
    const [data, setData] = useState({ sites: [], functions: [], parties: [] });
    const [siteId, setSiteId] = useState(null);
    const load = useCallback(() => getSites().then((d) => {
        setData(d);
        setSiteId((cur) => cur || d.sites[0]?.id);
    }).catch((e) => toast.error(e?.response?.data?.error || 'Sahalar alınamadı')), []);
    useEffect(() => { load(); }, [load]);
    const save = (body, msg) => saveSite(body).then(() => { toast.success(msg || 'Kaydedildi'); load(); })
        .catch((e) => toast.error(e?.response?.data?.error || 'Kaydedilemedi'));
    const site = data.sites.find((s) => s.id === siteId);
    // a company that is not in the list yet can be added here (full list: Tanımlamalar > Firmalar)
    const [newCo, setNewCo] = useState(null);
    const [types, setTypes] = useState([]);
    useEffect(() => { getCompanies().then((m) => setTypes(m.party_types.filter((t) => t.is_active))).catch(() => {}); }, []);
    const addCompany = () => saveCompany(0, { name: newCo.name.trim(), party_types: newCo.types, is_active: true })
        .then(() => { toast.success('Firma eklendi'); setNewCo(null); load(); })
        .catch((e) => toast.error(e?.response?.data?.error || 'Firma eklenemedi'));
    const fname = (code) => data.functions.find((f) => f.code === code)?.name_tr || code;
    const opOf = (fn) => site?.operators.find((o) => o.function === fn);
    const usedFunctions = site ? [...new Set(site.zones.map((z) => z.function).filter((f) => f && f !== 'SANAL'))] : [];
    const hasProduction = site?.zones.some((z) => z.function === 'URETIM');
    return (
        <div>
            <Breadcrumb parent="Depo Yönetimi" current="Saha ve Operatörler" />
            <Row>
                <Col md="3">
                    <Card><CardBody style={{ maxHeight: 760, overflowY: 'auto' }}>
                        {data.sites.map((s) => (
                            <div key={s.id} onClick={() => setSiteId(s.id)} className="p-2"
                                style={{ cursor: 'pointer', borderRadius: 4, background: s.id === siteId ? '#e8ebf7' : 'transparent' }}>
                                <b>{s.name}</b><div className="dt-muted">{SITE_TYPE[s.site_type]} · {s.operator_name || 'operatör yok'}</div>
                            </div>
                        ))}
                    </CardBody></Card>
                </Col>
                <Col md="9">
                    {site && (
                        <>
                            <Card><CardBody>
                                <h5>{site.name} <span className="dt-muted">{site.code}</span></h5>
                                <Row>
                                    <Col md="4"><Label>Saha tipi</Label>
                                        <Input type="select" value={site.site_type}
                                            onChange={(e) => save({ kind: 'site', warehouse_id: site.id, site_type: e.target.value, operator_party_id: site.operator_party_id || 0 })}>
                                            {Object.entries(SITE_TYPE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                        </Input></Col>
                                    <Col md="5"><Label>Saha operatörü</Label>
                                        <Input type="select" value={site.operator_party_id || ''}
                                            onChange={(e) => save({ kind: 'site', warehouse_id: site.id, site_type: site.site_type, operator_party_id: Number(e.target.value) || 0 })}>
                                            <option value="">— seçiniz —</option>
                                            {data.parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                        </Input></Col>
                                    <Col md="3" className="d-flex align-items-end">
                                        {site.site_type === 'FABRIKA' && !hasProduction && (
                                            <Button color="primary" outline onClick={() => save({ kind: 'productionZone', warehouse_id: site.id }, 'Üretim bölgesi eklendi')}>
                                                + Üretim bölgesi</Button>
                                        )}
                                    </Col>
                                </Row>
                            </CardBody></Card>
                            <Card><CardBody>
                                <h6>Bölgeler ve işlevleri</h6>
                                <table className="ct-table">
                                    <thead><tr><th>Kod</th><th>Bölge</th><th>WMS tipi</th><th>İşlev</th></tr></thead>
                                    <tbody>{site.zones.map((z) => (
                                        <tr key={z.id}><td>{z.code}</td><td><b>{z.name}</b>{!z.is_active && <Badge color="secondary" className="ml-1">pasif</Badge>}</td><td className="dt-muted">{z.zone_type}</td>
                                            <td><Input bsSize="sm" type="select" value={z.function || ''}
                                                onChange={(e) => save({ kind: 'zoneFunction', zone_id: z.id, function: e.target.value })}>
                                                <option value="">— yok —</option>
                                                {data.functions.map((f) => <option key={f.code} value={f.code}>{f.name_tr}</option>)}
                                            </Input></td></tr>
                                    ))}</tbody>
                                </table>
                                {site.zones.length === 0 && <div className="dt-muted">Bölge yok (Depo Yönetimi &gt; Bölge Tanımları).</div>}
                            </CardBody></Card>
                            <Card><CardBody>
                                <div className="d-flex align-items-center"><h6 className="mb-0">İşlev bazında operatör</h6>
                                    <Button size="sm" color="link" className="ml-auto" onClick={() => setNewCo(newCo ? null : { name: '', types: [] })}>+ Yeni firma</Button></div>
                                {newCo && (
                                    <div className="dt-draft mb-2">
                                        <Row>
                                            <Col md="5"><Label>Firma adı</Label><Input value={newCo.name} onChange={(e) => setNewCo({ ...newCo, name: e.target.value })} /></Col>
                                            <Col md="5"><Label>Roller</Label>
                                                <div>{types.map((t) => (
                                                    <label key={t.code} className="mr-2"><input type="checkbox" checked={newCo.types.includes(t.code)}
                                                        onChange={(e) => setNewCo({ ...newCo, types: e.target.checked ? [...newCo.types, t.code] : newCo.types.filter((x) => x !== t.code) })} /> {t.name_tr}</label>
                                                ))}</div></Col>
                                            <Col md="2" className="d-flex align-items-end"><Button color="primary" disabled={!newCo.name.trim() || newCo.types.length === 0} onClick={addCompany}>Ekle</Button></Col>
                                        </Row>
                                    </div>
                                )}
                                <div className="dt-muted mb-2">Boş bırakılan işlevi saha operatörü yürütür. Farklı firmaların yürüttüğü bölgeler arası hareket devir-teslimdir.</div>
                                <table className="ct-table">
                                    <thead><tr><th>İşlev</th><th>Operatör</th></tr></thead>
                                    <tbody>{usedFunctions.map((fn) => (
                                        <tr key={fn}><td>{fname(fn)}</td>
                                            <td><Input bsSize="sm" type="select" value={opOf(fn)?.party_id || ''}
                                                onChange={(e) => save({ kind: 'siteOperator', warehouse_id: site.id, function: fn, party_id: Number(e.target.value) || 0 })}>
                                                <option value="">saha operatörü ({site.operator_name || '—'})</option>
                                                {data.parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                            </Input></td></tr>
                                    ))}</tbody>
                                </table>
                            </CardBody></Card>
                            <Card><CardBody>
                                <h6>Devir-teslim lokasyonları (otomatik)</h6>
                                <table className="ct-table">
                                    <thead><tr><th>Lokasyon</th><th>Tip</th><th>Operatör</th></tr></thead>
                                    <tbody>{site.locations.map((l) => (
                                        <tr key={l.id}><td>{l.name}</td><td>{l.type}</td><td>{l.operator || '—'}</td></tr>
                                    ))}</tbody>
                                </table>
                            </CardBody></Card>
                        </>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default SahaOperatorler;
